<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import {
  BellRing,
  Building2,
  Check,
  Copy,
  KeyRound,
  MessageCircle,
  RefreshCw,
  Save,
  ServerCog,
  ShieldCheck,
} from "@lucide/vue";
import { api } from "../api";
import PageHeader from "../components/PageHeader.vue";
import UiTabs from "../ui/UiTabs.vue";
import UiToast from "../ui/UiToast.vue";

const active = ref("whatsapp");
const saving = ref(false);
const generating = ref(false);
const toast = ref("");
const toastType = ref("success");
const copied = ref("");

const whatsapp = reactive({
  enabled: false,
  graphApiVersion: "v25.0",
  businessAccountId: "",
  phoneNumberId: "",
  accessToken: "",
  templateLanguage: "tr",
  accessTemplate: "student_access_notification",
  defaultCountryCode: "90",
  accessTokenConfigured: false,
});

const governorate = reactive({
  enabled: false,
  apiKey: "",
  apiKeyConfigured: false,
  keyLastFour: "",
  endpoint: "",
  authenticationHeader: "X-API-Key",
});

const settingTabs = [
  { value: "whatsapp", label: "WhatsApp API", icon: MessageCircle },
  { value: "governorate", label: "Valilik API", icon: Building2 },
  { value: "system", label: "Sistem", icon: ServerCog },
  { value: "notifications", label: "Bildirimler", icon: BellRing },
];

const curlExample = computed(() =>
  governorate.endpoint
    ? `curl -H "${governorate.authenticationHeader}: ${governorate.apiKey || "API_ANAHTARI"}" "${governorate.endpoint}"`
    : "",
);

function show(message, type = "success") {
  toast.value = message;
  toastType.value = type;
  setTimeout(() => (toast.value = ""), 3200);
}

async function load() {
  try {
    const [whatsappResult, governorateResult] = await Promise.all([
      api("/api/settings/whatsapp"),
      api("/api/settings/governorate"),
    ]);
    Object.assign(whatsapp, whatsappResult.whatsapp);
    Object.assign(governorate, governorateResult.governorate);
  } catch (error) {
    show(error.message, "error");
  }
}

async function saveWhatsapp() {
  saving.value = true;
  try {
    Object.assign(
      whatsapp,
      (
        await api("/api/settings/whatsapp", {
          method: "PUT",
          body: JSON.stringify(whatsapp),
        })
      ).whatsapp,
    );
    whatsapp.accessToken = "";
    show("WhatsApp ayarları güvenli biçimde kaydedildi.");
  } catch (error) {
    show(error.message, "error");
  } finally {
    saving.value = false;
  }
}

async function saveGovernorate() {
  saving.value = true;
  try {
    Object.assign(
      governorate,
      (
        await api("/api/settings/governorate", {
          method: "PUT",
          body: JSON.stringify({
            enabled: governorate.enabled,
            apiKey: governorate.apiKey,
          }),
        })
      ).governorate,
    );
    show("Valilik API ayarları kaydedildi.");
  } catch (error) {
    show(error.message, "error");
  } finally {
    saving.value = false;
  }
}

async function regenerateKey() {
  generating.value = true;
  try {
    const result = await api("/api/settings/governorate/regenerate-key", {
      method: "POST",
    });
    Object.assign(governorate, result.governorate, {
      apiKey: result.apiKey,
    });
    await copyText(result.apiKey, "key");
    show("Yeni API anahtarı üretildi ve panoya kopyalandı.");
  } catch (error) {
    show(error.message, "error");
  } finally {
    generating.value = false;
  }
}

async function copyText(value, target) {
  if (!value) return show("Kopyalanacak değer bulunamadı.", "error");
  try {
    await navigator.clipboard.writeText(value);
    copied.value = target;
    setTimeout(() => (copied.value = ""), 1800);
  } catch {
    show("Tarayıcı panoya erişemedi.", "error");
  }
}

onMounted(load);
</script>

<template>
  <PageHeader
    title="Sistem ayarları"
    description="Erva Spor Okulları genelindeki entegrasyon ve veri paylaşım tercihlerini yönetin."
  />

  <section class="panel overflow-hidden">
    <div class="px-6 pt-2">
      <UiTabs v-model="active" :items="settingTabs" />
    </div>

    <div class="p-6">
      <form
        v-if="active === 'whatsapp'"
        class="space-y-6"
        @submit.prevent="saveWhatsapp"
      >
        <div class="flex items-center justify-between rounded-lg border bg-slate-50 p-4">
          <div class="flex items-center gap-3">
            <span class="grid size-10 place-items-center rounded-md bg-emerald-100 text-emerald-700">
              <ShieldCheck class="size-5" />
            </span>
            <div>
              <strong class="block text-sm text-ink-950">WhatsApp Business Cloud API</strong>
              <span class="text-xs text-slate-500">Veli giriş ve çıkış bildirimleri</span>
            </div>
          </div>
          <span :class="whatsapp.accessTokenConfigured ? 'chip-success' : 'chip-neutral'">
            {{ whatsapp.accessTokenConfigured ? "Token kayıtlı" : "Token bekleniyor" }}
          </span>
        </div>

        <label class="flex items-center justify-between rounded-lg border p-4">
          <div>
            <strong class="block text-sm text-ink-950">Veli bildirimlerini etkinleştir</strong>
            <span class="text-xs text-slate-500">Onaylı şablon üzerinden otomatik mesaj gönderir.</span>
          </div>
          <input v-model="whatsapp.enabled" type="checkbox" class="size-5 accent-brand-600" />
        </label>

        <div class="grid gap-4 md:grid-cols-3">
          <label class="field"><span class="field-label">Graph API sürümü</span><input v-model="whatsapp.graphApiVersion" class="input" /></label>
          <label class="field"><span class="field-label">Phone Number ID</span><input v-model="whatsapp.phoneNumberId" class="input font-mono" /></label>
          <label class="field"><span class="field-label">Business Account ID</span><input v-model="whatsapp.businessAccountId" class="input font-mono" /></label>
          <label class="field md:col-span-3">
            <span class="field-label">Access token</span>
            <input
              v-model="whatsapp.accessToken"
              type="password"
              class="input"
              :placeholder="whatsapp.accessTokenConfigured ? 'Boş bırakılırsa mevcut token korunur' : 'Token girin'"
            />
          </label>
          <label class="field"><span class="field-label">Şablon dili</span><input v-model="whatsapp.templateLanguage" class="input" /></label>
          <label class="field"><span class="field-label">Mesaj şablonu</span><input v-model="whatsapp.accessTemplate" class="input" /></label>
          <label class="field"><span class="field-label">Varsayılan ülke kodu</span><input v-model="whatsapp.defaultCountryCode" class="input" /></label>
        </div>

        <div class="flex justify-end border-t pt-5">
          <button class="btn-primary" :disabled="saving">
            <Save class="size-4" />{{ saving ? "Kaydediliyor..." : "Ayarları Kaydet" }}
          </button>
        </div>
      </form>

      <form
        v-else-if="active === 'governorate'"
        class="space-y-6"
        @submit.prevent="saveGovernorate"
      >
        <div class="flex flex-wrap items-center justify-between gap-4 rounded-lg border bg-slate-50 p-4">
          <div class="flex items-center gap-3">
            <span class="grid size-10 place-items-center rounded-md bg-sky-100 text-sky-700">
              <Building2 class="size-5" />
            </span>
            <div>
              <strong class="block text-sm text-ink-950">Kayseri Valiliği Veri API’si</strong>
              <span class="text-xs text-slate-500">Tüm okullar için güncel ve toplulaştırılmış JSON istatistikleri</span>
            </div>
          </div>
          <span :class="governorate.enabled && governorate.apiKeyConfigured ? 'chip-success' : 'chip-neutral'">
            {{ governorate.enabled && governorate.apiKeyConfigured ? "Yayında" : "Pasif" }}
          </span>
        </div>

        <label class="flex items-center justify-between rounded-lg border p-4">
          <div>
            <strong class="block text-sm text-ink-950">Valilik API yayınını etkinleştir</strong>
            <span class="text-xs text-slate-500">Anahtarı doğrulanan istekler anlık JSON sonuç alabilir.</span>
          </div>
          <input v-model="governorate.enabled" type="checkbox" class="size-5 accent-brand-600" />
        </label>

        <div class="space-y-4">
          <label class="field">
            <span class="field-label">API adresi</span>
            <div class="flex gap-2">
              <input :value="governorate.endpoint" class="input min-w-0 flex-1 font-mono text-xs" readonly />
              <button type="button" class="btn-secondary shrink-0" title="API adresini kopyala" @click="copyText(governorate.endpoint, 'endpoint')">
                <Check v-if="copied === 'endpoint'" class="size-4" /><Copy v-else class="size-4" />
              </button>
            </div>
          </label>

          <label class="field">
            <span class="field-label">API anahtarı</span>
            <div class="flex flex-col gap-2 lg:flex-row">
              <input v-model="governorate.apiKey" class="input min-w-0 flex-1 font-mono text-xs" placeholder="En az 24 karakterlik anahtar girin veya otomatik üretin" />
              <button type="button" class="btn-secondary shrink-0" :disabled="!governorate.apiKey" @click="copyText(governorate.apiKey, 'key')">
                <Check v-if="copied === 'key'" class="size-4" /><Copy v-else class="size-4" /> Kopyala
              </button>
              <button type="button" class="btn-secondary shrink-0" :disabled="generating" @click="regenerateKey">
                <RefreshCw class="size-4" :class="generating && 'animate-spin'" /> Yeni Anahtar Üret
              </button>
            </div>
          </label>
        </div>

        <div class="rounded-lg border border-slate-200 bg-slate-950 p-4 text-slate-100">
          <div class="mb-3 flex items-center gap-2 text-xs font-bold text-slate-300">
            <KeyRound class="size-4" /> İstek örneği
          </div>
          <code class="block overflow-x-auto whitespace-nowrap text-xs">{{ curlExample }}</code>
        </div>

        <div class="rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900">
          JSON çıktısı aktif/pasif okul ve öğrenci sayılarını, içeride bulunan öğrenci sayısını, bugünkü giriş-çıkışları, kartları, cihazları ve okul bazlı özetleri içerir. Öğrenci adı, telefon veya kart UID gibi kişisel veriler paylaşılmaz.
        </div>

        <div class="flex justify-end border-t pt-5">
          <button class="btn-primary" :disabled="saving">
            <Save class="size-4" />{{ saving ? "Kaydediliyor..." : "Ayarları Kaydet" }}
          </button>
        </div>
      </form>

      <div v-else class="grid min-h-80 place-items-center text-center">
        <div>
          <component :is="active === 'system' ? ServerCog : BellRing" class="mx-auto mb-4 size-10 text-slate-300" />
          <h3 class="font-extrabold text-ink-950">{{ active === "system" ? "Sistem ayarları" : "Bildirim kanalları" }}</h3>
          <p class="mt-1 text-sm text-slate-500">Bu bölüm yeni servisler için hazır.</p>
        </div>
      </div>
    </div>
  </section>

  <UiToast :message="toast" :type="toastType" @close="toast = ''" />
</template>
